<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title mb-0">Precios base</h1>
    </div>

    <div class="table-container">
      <!-- Cabecera estándar: dirección + recargar + buscador, como en el Tarifario -->
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <div class="d-flex mb-2 mb-md-0">
            <button
              type="button"
              :class="['button', direction === 'OUT' ? 'button-primary' : 'button-light']"
              @click="setDirection('OUT')"
            >
              Distrito → Aeropuerto
            </button>
            <button
              type="button"
              :class="['button ml-2', direction === 'IN' ? 'button-primary' : 'button-light']"
              @click="setDirection('IN')"
            >
              Aeropuerto → Distrito
            </button>
          </div>

          <div class="d-flex align-items-center">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover
                title="Recargar" @click="loadGrouped()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <SearchBar placeholder="Buscar distrito..." class="ml-md-2" @search="search = $event"
              @input="search = $event" />
          </div>
        </div>
      </div>

      <!-- Acordeones por provincia -->
      <div class="tarifa-body">
        <template v-if="visibleProvinces.length">
          <ProvinceAccordion
            v-for="prov in visibleProvinces" :key="prov.name"
            :province="prov"
            :expanded="isExpanded(prov)"
            :vehicle-types="vehicleTypes"
            :route-type="routeType"
            :search="search"
            @toggle="toggle(prov.name)"
            @saved="onSaved"
          />
        </template>
        <div v-else-if="!loading" class="tarifa-empty">
          <template v-if="search">No se encontró ningún distrito con ese nombre.</template>
          <template v-else>
            No hay distritos activos en esta ciudad. Ve a Geografía → Distritos para activar.
          </template>
        </div>
      </div>

      <!-- Footer resumen (en vivo) -->
      <div class="tarifa-footer">
        {{ liveTotalActive }} distritos activos en {{ activeCityName }} ·
        {{ liveTotalWithPrice }} con precio
      </div>
    </div>
  </div>
</template>

<script>
import { BasePriceService } from '@/core/services'
import { Alert, SearchBar } from 'wize-admin'
import ProvinceAccordion from '../Components/ProvinceAccordion.vue'

const VEHICLE_TYPES = ['Sedan', 'SUV', 'VAN', 'Sprinter']

export default {
  name: 'BasePriceList',
  components: { ProvinceAccordion, SearchBar },
  data() {
    return {
      vehicleTypes: VEHICLE_TYPES,
      direction: 'OUT', // OUT = distrito→apto ; IN = apto→distrito
      provinces: [],
      expandedMap: {},
      search: '',
      loading: false
    }
  },
  computed: {
    routeType() {
      return this.direction === 'IN' ? 'airport_to_district' : 'district_to_airport'
    },
    // La ciudad sale del switcher del header (nivel 2). El módulo cuelga del
    // CITY_MENU, así que aquí siempre hay una ciudad elegida.
    activeCityName() {
      const c = this.$store.getters['city/getCurrentCity']
      return c ? c.name : ''
    },
    // Provincias visibles según la búsqueda (oculta las que no tienen match).
    visibleProvinces() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.provinces
      return this.provinces.filter((p) => p.districts.some((d) => d.name.toLowerCase().includes(q)))
    },
    // Totales en vivo (reflejan las ediciones inline sin recargar).
    liveTotalActive() {
      return this.provinces.reduce((n, p) => n + p.districts.length, 0)
    },
    liveTotalWithPrice() {
      return this.provinces.reduce(
        (n, p) => n + p.districts.filter((d) => d.prices && Object.keys(d.prices).length > 0).length, 0)
    }
  },
  methods: {
    isExpanded(prov) {
      // Buscando: expandir toda provincia con match. Si no: estado manual.
      if (this.search.trim()) return true
      return !!this.expandedMap[prov.name]
    },
    toggle(name) {
      this.$set(this.expandedMap, name, !this.expandedMap[name])
    },
    async loadGrouped() {
      try {
        this.loading = true
        await this.$store.dispatch('app/loading', true)
        const res = await BasePriceService.getBasePricesGrouped(this.direction)
        const payload = res.payload || {}
        this.provinces = payload.provinces || []
        // Primer acordeón expandido, resto colapsado (CA-09).
        this.expandedMap = {}
        if (this.provinces.length) this.$set(this.expandedMap, this.provinces[0].name, true)
      } catch (e) {
        console.error(e)
        Alert.error(e && e.errors ? e.errors.message : 'Error al cargar el tarifario')
      } finally {
        this.loading = false
        await this.$store.dispatch('app/loading', false)
      }
    },
    setDirection(dir) {
      if (dir === this.direction) return
      this.direction = dir
      this.search = ''
      this.loadGrouped()
    },
    // Actualiza el precio en memoria tras un auto-save exitoso (mantiene conteos vivos).
    onSaved({ districtId, vehicleType, amount }) {
      for (const p of this.provinces) {
        const d = p.districts.find((x) => x.district_id === districtId)
        if (!d) continue
        if (!d.prices) this.$set(d, 'prices', {})
        if (amount == null) this.$delete(d.prices, vehicleType)
        else this.$set(d.prices, vehicleType, amount)
        break
      }
    }
  },
  async mounted() {
    await this.loadGrouped()
  }
}
</script>

<style lang="stylus" scoped>
// Los acordeones no existen en wize-admin; viven dentro de la card estándar y
// comparten estilo con el tarifario de proveedores (ProviderFaresPanel).
.tarifa-body
  padding 12px
.tarifa-empty
  text-align center
  color #6c757d
  padding 40px 16px
  font-size 14px
.tarifa-footer
  padding 12px 16px
  border-top 0.3px solid #e5e7eb
  font-size 13px
  color #6c757d
</style>
